import React, { useState } from 'react';
import * as bunnyLibrary from '../../services/adminBunnyLibrary.service';
import { uploadImage } from '../../services/upload.service';

/**
 * Drag-and-drop (or paste-a-link) thumbnail setter for a Bunny video —
 * shared between the Video Library page and any other screen that has a
 * bunny_video_id handy (e.g. a Hub event whose replay already exists),
 * so staff never need Google Drive or a Bunny.net login to do this.
 */
export default function ThumbnailDropzone({ videoId, onSaved, onError, onCancel }) {
  const [urlDraft, setUrlDraft] = useState('');
  const [altDraft, setAltDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const save = async (url) => {
    const finalUrl = (url ?? urlDraft).trim();
    if (!finalUrl) return;
    setSaving(true);
    try {
      await bunnyLibrary.setVideoThumbnail(videoId, finalUrl, altDraft.trim());
      onSaved?.();
    } catch (err) {
      onError?.(err.response?.data?.error || 'Could not set that thumbnail.');
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      onError?.("That file isn't an image — try a JPEG, PNG, GIF, or WEBP.");
      return;
    }
    setSaving(true);
    try {
      const url = await uploadImage(file);
      setUrlDraft(url);
      await save(url);
    } catch (err) {
      onError?.(err.response?.data?.error || 'Could not upload that image.');
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
        className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg py-4 px-2 text-center cursor-pointer transition-colors
          ${dragOver ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-200 hover:border-reset-terracotta'}`}
      >
        <span className="text-xs font-medium text-gray-500">
          {saving ? 'Uploading…' : 'Drop an image here, or click to choose'}
        </span>
        <input type="file" accept="image/*" className="hidden" disabled={saving}
          onChange={(e) => handleFile(e.target.files?.[0])} />
      </label>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-px bg-gray-100" />
        <span className="text-[10px] text-gray-300 uppercase tracking-wide">or paste a link</span>
        <div className="flex-1 h-px bg-gray-100" />
      </div>
      <input value={urlDraft} onChange={e => setUrlDraft(e.target.value)}
        placeholder="Image URL (Google Drive share links work)"
        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      <input value={altDraft} onChange={e => setAltDraft(e.target.value)}
        placeholder="Alt text (what's in the image, for accessibility)"
        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
      <div className="flex justify-end gap-2">
        {onCancel && (
          <button onClick={onCancel} className="text-xs text-gray-400 hover:text-reset-brown">Cancel</button>
        )}
        <button onClick={() => save()} disabled={saving || !urlDraft.trim()}
          className="text-xs font-medium text-reset-terracotta hover:underline disabled:opacity-50">
          {saving ? 'Saving…' : 'Save link'}
        </button>
      </div>
    </div>
  );
}
